import emergencyPorts from "../../../../packages/config/ports.json";
import Fokuszeit from './Fokuszeit';
import { useUiPreferences } from '../lib/useUiPreferences';
import { AccountControl } from './AccountControl';
import KopfMenue from './KopfMenue';
import KiLeiste from './KiLeiste';
import { nurHausZugaenge } from '../lib/kiLeiste';
import { kiFuellFarbe } from '../lib/kiGrenzen';
import { useKopfEinklappen } from '../lib/useKopfEinklappen';
import { useKopfEinzeilig } from '../lib/useKopfEinzeilig';
import '../styles/kopf-zeile.css';
import { usePartnerCounts } from '../hooks/usePartnerCounts';
import { PartnerOffenChip, GruppenraumKnopf, partnerOffenText, PARTNER_OFFEN_FARBE, type PartnerOffenZiel } from './PartnerOpenBanner';
import { type UsageAccount, type AccountHealth, kiSammelStand } from '../lib/kiSammelStand';
import { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import { type Project, ACCOUNTS, accountAccessLabel, accountAccessShortLabel } from '../types';
import { useAuth } from '../contexts/AuthContext';
import AnsichtMenue from './AnsichtMenue';
import WasWirktAufMich from './WasWirktAufMich';
import StageSharesInbox from './StageSharing';
import { VersionBadge } from './VersionBadge';
import TagNachtSchalter from './TagNachtSchalter';
import type { GuideAnordnungModus } from '../../shared/guide-anordnung';

interface ProjectTabsProps {
  projects: Project[];
  activeId: string;
  attention?: Record<string, 'working' | 'waiting' | 'needs_attention' | 'idle'>;
  missingSessions?: number;
  onSelect: (id: string) => void;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  missionActive?: boolean;
  onMissionClick?: () => void;
  allChatsActive?: boolean;
  onAllChatsClick?: () => void;
  /** Guide-Spalte (GD): die EINE anstehende Entscheidung, links neben dem Workspace. */
  guideActive?: boolean;
  /** Guide-Spalte eingeklappt? Nur bei offenem Guide gesetzt (Menuepunkt „Spalte links" in „Ansicht ▾"). */
  guideEingeklappt?: boolean;
  onGuideClick?: () => void;
  /** Total open (non-finished, non-sub) chats across all workspaces — rendered next to AC. */
  openChatsTotal?: number;
  /** Same rule, per projectId — rendered as "(N)" after each tab's name. */
  openCountByProject?: Record<string, number>;
  screenId?: string;
  onScreenChange?: (id: string) => void;
  /**
   * Die vier Anordnungen auch oben in der Leiste — erreichbar, wenn die Guide-Spalte
   * zu oder eingeklappt ist (Rafael, 25.09.2026). Die Wahl selbst (Merken, Guide
   * oeffnen) macht der Aufrufer; hier steht nur der Knopf.
   */
  anordnungModus?: GuideAnordnungModus;
  /** Mit gekoppeltem Bildschirm 2 gilt „nebeneinander" fest. */
  anordnungFestgelegt?: boolean;
  onAnordnung?: (m: GuideAnordnungModus) => void;
  /** Zustand, der in der Leiste sichtbar bleiben muss (z. B. „Bildschirm 2 verbunden"). */
  kopfStatus?: React.ReactNode;
  /** Seltene Ansichts-Knoepfe fuer das ⋯-Menue (zweiter Bildschirm, Standardaufteilung). */
  menueInhalt?: React.ReactNode;
  /** Klick auf „● N offen": oeffnet Posteingang bzw. Feedback (vorher der Streifen ueber der Leiste). */
  onPartnerOffen?: (component: PartnerOffenZiel) => void;
  /** Nur bei uiMode „sales": Rueckweg in den Sales-Bereich als Chip „← Sales". */
  onZurueckZuSales?: () => void;
  onKalender?: () => void;
}

type SyncState = 'idle' | 'syncing' | 'done' | 'error';
type LayoutClickState = 'idle' | 'running' | 'done' | 'nothing' | 'error';

/** Beschriftung und Hervorhebung des Guide-Knopfs fuer Partner (siehe `partnerGuideKnopf`). */
export const GUIDE_KNOPF_TEXT = '◆ Guide';
export const GUIDE_KNOPF_ERKLAERUNG = 'Guide öffnen: deine Berater und die Entscheidungen, die auf dich warten';
const GUIDE_KNOPF_HERVORGEHOBEN: React.CSSProperties = {
  background: 'var(--tn-gold)',
  border: '1px solid var(--tn-gold)',
  borderRadius: 4,
  boxShadow: '0 0 0 2px rgba(210,168,69,0.35)',
};

// --- Mode Detection (from Electron preload or URL param) ---
type CuiMode = 'remote' | 'local' | 'dev';
const MODE_COLORS: Record<CuiMode, string> = {
  remote: 'var(--tn-orange)',  // orange — remote server
  local: 'var(--tn-green)',   // green — local copy
  dev: 'var(--tn-purple)',     // purple — development
};
function detectCuiMode(): CuiMode {
  // URL param set by main.cjs is most reliable (preload may not get argv in sandbox)
  const fromUrl = new URLSearchParams(window.location.search).get('mode');
  if (fromUrl === 'local' || fromUrl === 'dev' || fromUrl === 'remote') return fromUrl;
  const fromElectron = (window as any).electronAPI?.mode;
  if (fromElectron) return fromElectron as CuiMode;
  return 'remote';
}
const CUI_MODE = detectCuiMode();

/** KI-Fuellstand im Menue „KI-Zugänge & Verbrauch": gemeinsame Grenzen 80/95 (lib/kiGrenzen.ts). */
const usagePctColor = (pct: number): string => kiFuellFarbe(pct);

// Shorten reset strings: "Zurücksetzung in 1 Std. 59 Min." → "1h59m", "Zurücksetzung Sa., 09:00" → "Sa 09:00"
// Also handles English: "Resets in 5 hr 59 min" → "5h59m", "Resets Tue 9:59 AM" → "Tue 9:59"
function shortenReset(raw: string): string {
  if (!raw) return '';
  // "in X Std. Y Min." or "in X hr Y min"
  const durMatch = raw.match(/(\d+)\s*(?:Std\.|hr)\s*(\d+)\s*(?:Min\.|min)/);
  if (durMatch) return `${durMatch[1]}h${durMatch[2]}m`;
  // "in Y Min." or "in Y min"
  const minOnly = raw.match(/in\s*(\d+)\s*(?:Min\.|min)/);
  if (minOnly) return `${minOnly[1]}m`;
  // Weekday + time: "Sa., 09:00" or "Tue 9:59 AM"
  const dayMatch = raw.match(/(Mo|Di|Mi|Do|Fr|Sa|So|Mon|Tue|Wed|Thu|Fri|Sat|Sun)\.?,?\s*([\d:]+)/i);
  if (dayMatch) return `${dayMatch[1]} ${dayMatch[2]}`;
  return raw.replace(/^(Zurücksetzung|Resets?)\s*/i, '').trim();
}

function CodexUsagePill({ account }: { account: UsageAccount }) {
  const windows = account.quotaWindows ?? [];
  const color = ACCOUNTS.find(a => a.id === account.accountId)?.color ?? 'var(--tn-text-muted)';
  const duration = (minutes: number) => minutes % 1440 === 0 ? `${minutes / 1440}d`
    : minutes % 60 === 0 ? `${minutes / 60}h` : `${minutes}m`;
  const resetIn = (seconds: number) => {
    const minutes = Math.max(0, Math.ceil((seconds * 1000 - Date.now()) / 60000));
    return minutes >= 1440 ? `in ${Math.floor(minutes / 1440)}d ${Math.floor(minutes % 1440 / 60)}h`
      : minutes >= 60 ? `in ${Math.floor(minutes / 60)}h ${minutes % 60}m` : `in ${minutes}m`;
  };
  const tooltip = [accountAccessLabel(account.accountId), 'Kontingent genutzt', ...windows.map(w =>
    `${duration(w.windowDurationMins)}: ${w.usedPercent}%; naechste Freigabe ${new Date(w.resetsAt * 1000).toLocaleString()}`),
    account.quotaError ? 'Kapazitaetsdaten nicht verfuegbar — Betrieb pruefen lassen.' : null, account.scrapedTimestamp ? `Gemessen: ${new Date(account.scrapedTimestamp).toLocaleString()}` : null,
  ].filter(Boolean).join('\n');
  return <div title={tooltip} style={{ display: 'flex', alignItems: 'center', gap: 4,
    padding: '2px 5px', borderRadius: 4, flexShrink: 0, background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--tn-border)' }}>
    <span style={{ fontSize: 9, fontWeight: 700, color }}>{accountAccessShortLabel(account.accountId)}</span>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {windows.map(w => <div key={w.windowDurationMins} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={{ width: 36, height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
          <div style={{ width: `${Math.min(100, w.usedPercent)}%`, height: '100%', background: usagePctColor(w.usedPercent) }} />
        </div>
        <span style={{ fontSize: 8, fontFamily: 'monospace', color: usagePctColor(w.usedPercent), whiteSpace: 'nowrap' }}>
          {w.usedPercent}% · {duration(w.windowDurationMins)} · {resetIn(w.resetsAt)}
        </span>
      </div>)}
      {!windows.length && <span style={{ fontSize: 8, color: 'var(--tn-text-muted)' }}>Auslastung unbekannt</span>}
    </div>
  </div>;
}

// --- Usage Pill (single account) ---
function UsagePill({ account, health }: { account: UsageAccount; health?: AccountHealth }) {
  if (account.provider === 'codex') return <CodexUsagePill account={account} />;
  const accountDef = ACCOUNTS.find(a => a.id === account.accountId);
  const color = accountDef?.color || 'var(--tn-text-muted)';
  const s = account.scraped;
  const displayName = accountAccessLabel(account.accountId);
  const shortName = accountAccessShortLabel(account.accountId);
  // null = nicht gemessen (siehe scripts/scrape-claude-usage.ts). Bewusst NICHT
  // auf 0 gefaltet: eine 0 sieht aus wie "frei" und hat genau deshalb ungemessene
  // Konten an die Spitze der Auto-Wahl sortiert (04.09.2026).
  const weeklyPct = s?.weeklyAllModels.percent ?? null;
  const sessionPct = s?.currentSession.percent ?? null;
  const ungemessen = weeklyPct === null || sessionPct === null;
  const weeklyColor = weeklyPct === null ? 'var(--tn-text-muted)' : usagePctColor(weeklyPct);
  const sessionColor = sessionPct === null ? 'var(--tn-text-muted)' : usagePctColor(sessionPct);

  const weeklyReset = s ? shortenReset(s.weeklyAllModels.resetDate) : '';
  const sessionReset = s ? shortenReset(s.currentSession.resetIn) : '';

  const drift = health?.status === 'drift';
  // Was die CLI selbst gemeldet hat. Ohne das stand KI 2 am 28.09.2026 gruen bei 67 %,
  // waehrend jeder Fable-Start dort abgewiesen wurde (eigenes Fable-Wochenlimit).
  const kontoGesperrt = !!account.cliSperre;
  const modellSperren = account.modellSperren ?? [];
  const sperrLines = [
    kontoGesperrt ? `⛔ Gesperrt laut CLI bis ${new Date(account.cliSperre!.until).toISOString().slice(0, 16).replace('T', ' ')}Z` : null,
    ...modellSperren.map(m => `⛔ ${m.text} — andere Modelle frei`),
  ].filter((x): x is string => !!x);
  const driftTooltipLines = drift
    ? [
        '',
        '⚠ Zugangskonfiguration widerspruechlich — Betrieb pruefen lassen.',
      ]
    : [];

  const tooltip = (s
    ? [
        displayName,
        'Kontingent genutzt — naechste Freigabe je Zeitraum',
        `Kurzzeit: ${sessionPct === null ? 'unbekannt' : sessionPct + '%'} ${s.currentSession.resetIn || ''}`,
        `Woche: ${weeklyPct === null ? 'unbekannt' : weeklyPct + '%'} ${s.weeklyAllModels.resetDate || ''}`,
        ungemessen ? 'Keine Kapazitaetsmessung verfuegbar — Betrieb pruefen lassen.' : null,
      ].filter(Boolean)
    : [`${displayName}: Keine Kapazitaetsdaten`]
  ).concat(sperrLines, driftTooltipLines).join('\n');
  const rot = drift || kontoGesperrt;

  return (
    <div
      title={tooltip}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: '2px 5px',
        borderRadius: 4,
        background: rot
          ? 'rgba(247,118,142,0.18)'
          : account.status === 'critical'
            ? 'rgba(247,118,142,0.12)'
            : account.status === 'warning'
              ? 'rgba(224,175,104,0.08)'
              : 'rgba(255,255,255,0.03)',
        border: `1px solid ${rot ? 'var(--tn-red)' : modellSperren.length || account.status === 'critical' ? 'rgba(247,118,142,0.4)' : 'rgba(255,255,255,0.06)'}`,
        cursor: 'default', flexShrink: 0,
        WebkitAppRegion: 'no-drag',
      } as React.CSSProperties}
    >
      {drift && (
        <span
          aria-label="Zugangskonfiguration pruefen"
          style={{ fontSize: 9, color: 'var(--tn-red)', lineHeight: 1, marginRight: 1 }}
        >
          {'⚠'}
        </span>
      )}
      <span style={{ fontSize: 9, fontWeight: 700, color, letterSpacing: '0.02em', lineHeight: 1 }}>
        {shortName}
      </span>
      {kontoGesperrt && (
        <span aria-label="Zugang gesperrt" style={{ fontSize: 8, fontWeight: 700, color: 'var(--tn-red)', lineHeight: 1 }}>{'⛔'}</span>
      )}
      {!kontoGesperrt && modellSperren.map(m => (
        <span key={m.modell} aria-label={`${m.modell} gesperrt`}
          style={{ fontSize: 7, fontWeight: 700, color: 'var(--tn-red)', border: '1px solid color-mix(in srgb, var(--tnf-f7768e) 50%, transparent)', borderRadius: 2, padding: '0 2px', lineHeight: 1.2 }}>
          {`${m.modell.charAt(0).toUpperCase()}${m.modell.slice(1)} ⛔`}
        </span>
      ))}
      {s ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, width: 36 }}>
            <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 1.5, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${weeklyPct === null ? 0 : Math.min(100, weeklyPct)}%`, background: weeklyColor, borderRadius: 1.5, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 1, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${sessionPct === null ? 0 : Math.min(100, sessionPct)}%`, background: sessionColor, borderRadius: 1, transition: 'width 0.5s ease' }} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0, lineHeight: 1 }}>
            <span style={{ fontSize: 8, fontWeight: 600, fontFamily: 'monospace', color: weeklyColor, whiteSpace: 'nowrap' }}>
              {weeklyPct === null ? '?' : `${weeklyPct}%`} <span style={{ opacity: 0.6, fontWeight: 400 }}>{weeklyReset}</span>
            </span>
            <span style={{ fontSize: 7, fontFamily: 'monospace', color: sessionColor, opacity: 0.7, whiteSpace: 'nowrap' }}>
              {sessionPct === null ? '?' : `${sessionPct}%`} <span style={{ fontWeight: 400 }}>{sessionReset}</span>
            </span>
          </div>
        </>
      ) : (
        <span style={{ fontSize: 8, color: 'var(--tn-text-muted)', opacity: 0.5 }}>?</span>
      )}
    </div>
  );
}

/** „4P offline" sagte niemandem etwas (Rafael, 03.10.2026 09:37). Gemeint sind Panel-Dienste des Arbeitsbereichs. */
export function fensterAusText(n: number): string {
  return n === 1 ? '1 Fenster läuft nicht' : `${n} Fenster laufen nicht`;
}

// --- Usage Bars (all accounts) ---
const USAGE_CACHE_KEY = 'cui-usage-accounts';

function loadCachedAccounts(): UsageAccount[] {
  try {
    const raw = localStorage.getItem(USAGE_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function useKiZugaenge() {
  const [accounts, setAccounts] = useState<UsageAccount[]>(loadCachedAccounts);
  const [health, setHealth] = useState<Record<string, AccountHealth>>({});
  const pollRef = useRef<ReturnType<typeof setInterval>>(null);
  const healthPollRef = useRef<ReturnType<typeof setInterval>>(null);

  const fetchUsage = useCallback(() => {
    if (window.__cuiServerAlive !== true) return;
    fetch('/api/claude-code/stats-v2', { signal: AbortSignal.timeout(12000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.accounts) return;
        const filtered = data.accounts.filter((a: UsageAccount) => a.accountId !== 'local');
        setAccounts(filtered);
        try { localStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(filtered)); } catch { /* quota */ }
      })
      .catch(() => { /* server not ready or timeout — cached data stays visible */ });
  }, []);

  const fetchHealth = useCallback(() => {
    if (window.__cuiServerAlive !== true) return;
    fetch('/api/accounts/health', { signal: AbortSignal.timeout(8000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.accounts || !Array.isArray(data.accounts)) return;
        const next: Record<string, AccountHealth> = {};
        for (const a of data.accounts) {
          next[a.id] = {
            status: a.status,
            expected_org_id: a.expected_org_id,
            token_org_id: a.token_org_id,
            cookie_org_id: a.cookie_org_id,
          };
        }
        setHealth(next);
      })
      .catch(() => { /* endpoint not yet built / server restart — keep last state */ });
  }, []);

  useEffect(() => {
    fetchUsage();
    pollRef.current = setInterval(fetchUsage, 120_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchUsage]);

  useEffect(() => {
    fetchHealth();
    healthPollRef.current = setInterval(fetchHealth, 60_000);
    return () => { if (healthPollRef.current) clearInterval(healthPollRef.current); };
  }, [fetchHealth]);

  return { accounts, health };
}

function UsageBars({ accounts, health }: { accounts: UsageAccount[]; health: Record<string, AccountHealth> }) {
  if (accounts.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {accounts.map(acc => <UsagePill key={acc.accountId} account={acc} health={health[acc.accountId]} />)}
    </div>
  );
}

/** Inhalt des Menues „KI-Zugänge & Verbrauch" hinter der KI-Leiste (nur Betreiber; Partner haben kein Menue, KiLeiste.tsx). */
function KiMenueInhalt({ accounts, health, mitVerwaltung }: { accounts: UsageAccount[]; health: Record<string, AccountHealth>; mitVerwaltung: boolean }) {
  return <>
    <p style={{ margin: 0 }}>Kontingent genutzt je Zugang (oben Woche, unten Kurzzeit). „?" heißt: keine gültige Messung.</p>
    <UsageBars accounts={accounts} health={health} />
    {accounts.length === 0 && <p style={{ margin: 0 }}>Noch keine Kapazitätsdaten — unbekannt, nicht 0 %.</p>}
    {mitVerwaltung && <AccountControl eingebettet />}
  </>;
}

// --- Syncthing Toggle ---
/** Ab diesem Alter des letzten Abgleichs wird Syncthing oben als Warnung gezeigt. */
const SYNC_ALT_AB_MS = 2 * 3_600_000;

function useSyncthing(aktiv: boolean) {
  const [paused, setPaused] = useState<boolean | null>(null); // null = loading/unknown
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval>>(null);

  const fetchStatus = useCallback(() => {
    if (window.__cuiServerAlive !== true) return;
    fetch('/api/syncthing/status', { signal: AbortSignal.timeout(10000) })
      .then(r => {
        if (!r.ok) return null;
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setPaused(data.paused);
        if (data.lastSyncAt) setLastSync(data.lastSyncAt);
      })
      .catch(() => { /* timeout on slow connections */ });
  }, []);

  useEffect(() => {
    if (!aktiv) return;
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus, aktiv]);

  const toggle = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    if (toggling || paused === null) return;
    setToggling(true);
    try {
      const endpoint = paused ? '/api/syncthing/resume' : '/api/syncthing/pause';
      const res = await fetch(endpoint, { method: 'POST', signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`syncthing toggle ${res.status}`);
      const data = await res.json();
      setPaused(data.paused);
    } catch (err) {
      console.warn('[ProjectTabs] syncthingToggle:', err);
    }
    setToggling(false);
  }, [paused, toggling]);

  // Format last sync time as relative
  const lastSyncLabel = lastSync ? (() => {
    const diff = Date.now() - new Date(lastSync).getTime();
    if (diff < 60000) return 'gerade';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return `${Math.floor(diff / 86400000)}d`;
  })() : null;

  const alterMs = lastSync ? Date.now() - new Date(lastSync).getTime() : null;
  /** Etwas zu warnen: pausiert, oder der letzte Abgleich liegt lange zurueck. */
  const warnung = paused === true || (alterMs !== null && alterMs > SYNC_ALT_AB_MS);
  return { paused, lastSyncLabel, toggling, toggle, warnung };
}

type SyncthingZustand = ReturnType<typeof useSyncthing>;

function SyncthingToggle({ st }: { st: SyncthingZustand }) {
  const { paused, lastSyncLabel, toggling, toggle } = st;
  if (paused === null) return null; // Don't render until status known

  const color = paused ? 'var(--tn-fehler)' : 'var(--tn-ok)';

  return (
    <button
      data-ai-id="project-tabs-syncthing-toggle"
      onClick={toggle}
      disabled={toggling}
      title={paused
        ? `Syncthing PAUSIERT${lastSyncLabel ? ` — Letzter Sync: vor ${lastSyncLabel}` : ''}\nKlick zum Fortsetzen`
        : `Syncthing AKTIV${lastSyncLabel ? ` — Letzter Sync: vor ${lastSyncLabel}` : ''}\nKlick zum Pausieren`
      }
      style={{
        background: paused ? 'rgba(239,68,68,0.1)' : 'none',
        border: `1px solid ${color}`,
        color,
        padding: '3px 8px',
        fontSize: 10,
        fontWeight: 600,
        cursor: toggling ? 'wait' : 'pointer',
        borderRadius: 4,
        marginRight: 6,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s',
        opacity: toggling ? 0.5 : 1,
        WebkitAppRegion: 'no-drag',
      } as React.CSSProperties}
    >
      {paused ? 'ST paused' : 'ST'}{lastSyncLabel ? ` (${lastSyncLabel})` : ''}
    </button>
  );
}

// --- Workspace Category Navigation ---
interface WorkspaceCategory {
  id: string;
  label: string;
  icon: string;
  order: number;
}

const FALLBACK_CATEGORIES: WorkspaceCategory[] = [
  { id: 'apps', label: 'Apps', icon: '🚀', order: 1 },
  { id: 'sessions', label: 'Sessions', icon: '🎛', order: 2 },
  { id: 'devops', label: 'DevOps', icon: '🔧', order: 3 },
  { id: 'partner', label: 'Partner', icon: '🤝', order: 4 },
  { id: 'business', label: 'Business', icon: '📊', order: 5 },
  { id: 'maintenance', label: 'Maintenance', icon: '🛠', order: 6 },
  { id: 'stage', label: 'Stage', icon: '📁', order: 7 },
];

function getProjectCategory(project: Project): string {
  return (project as any).category || 'stage';
}

export default memo(function ProjectTabs({ projects, activeId, attention, missingSessions = 0, onSelect, onNew, onEdit, onDelete, guideActive, guideEingeklappt, onGuideClick, openCountByProject, screenId = '1', onScreenChange, anordnungModus, anordnungFestgelegt = false, onAnordnung, kopfStatus, menueInhalt, onPartnerOffen, onZurueckZuSales, onKalender }: ProjectTabsProps) {
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [wasWirktOffen, setWasWirktOffen] = useState(false);
  const [syncDetail, setSyncDetail] = useState('');
  const [layoutClickState, setLayoutClickState] = useState<LayoutClickState>('idle');
  const [layoutClickDetail, setLayoutClickDetail] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [allLive, setAllLive] = useState(false);
  const [panelHealth, setPanelHealth] = useState<{ running: number; total: number; missing: string[] } | null>(null);
  const ui = useUiPreferences();
  const { user, authEnabled, logout, canAccessWorkspace, canAccessPanel } = useAuth();
  /* Fuer einen Partner heisst der Knopf lesbar „◆ Guide" und leuchtet, solange der
   * Guide zu ist (Rafael, 23.09.2026: „mach den GUIDE modus auch mehr sichtbar und
   * selbsterklaerend"). Rafaels eigener Knopf bleibt „GD". */
  const partnerGuideKnopf = authEnabled === true && !!user && user.role !== 'admin';
  /*
   * BETRIEBSLEISTE NUR FUER DEN EIGENTUEMER (Rafael, 21.09.2026, Option a:
   * "Kontingente, Rebuild, Sync, Cache und den internen Verlauf fuer
   * Nicht-Admins ausblenden").
   *
   * Anlass, gemessen unter der Anmeldung eines Partners: in seiner Kopfzeile
   * standen unsere vier KI-Kontingente mit Prozentwerten und Ruecksetzzeiten,
   * dazu Knoepfe, die sich selbst als "Rebuild CUI frontend (npm run build +
   * restart server)" beschreiben. Ein Kunde konnte damit unseren Server neu
   * bauen und neu starten.
   *
   * Gleiches Muster wie beim Buehnenmodus-Schalter weiter unten: ohne
   * Anmeldung (Dev) aendert sich nichts.
   */
  const istBetreiber = !authEnabled || user?.role === 'admin';
  /* PARTNER OHNE BETRIEBSANZEIGEN (Vize Partner 02.11.2026 fuer fachpartner; KD 03.11.2026 auch fuer product-owner):
   * Kontingentleisten KI 1–4 und Build-Kennung sind Betriebswissen. Sichtbar nur fuer den Admin (Rafael) bzw. ohne
   * Anmeldung (Dev) — also genau dort, wo `istBetreiber` gilt; jede andere angemeldete Rolle sieht sie nicht. */
  const ohneBetriebsanzeigen = !istBetreiber;
  const syncthing = useSyncthing(istBetreiber);
  const ki = useKiZugaenge();
  // Partner: nur die vier Hauptzugaenge des Hauses zaehlen (wie ihre KI-Leiste).
  const kiStand = kiSammelStand(istBetreiber ? ki.accounts : nurHausZugaenge(ki.accounts), ki.health);
  const [kopfEingeklappt, setKopfEingeklappt] = useKopfEinklappen();
  const kopfZeileRef = useRef<HTMLDivElement>(null);
  const kopfEinzeilig = useKopfEinzeilig(kopfZeileRef);
  const partnerOffen = usePartnerCounts();

  // --- Category Navigation state ---
  const [categories, setCategories] = useState<WorkspaceCategory[]>(FALLBACK_CATEGORIES);
  // Toggle between Active-Bar (default, only working/needs_attention/active) and Full Categories View
  const [showAllWorkspaces, setShowAllWorkspaces] = useState<boolean>(false);
  const [lastWorkspacePerCat, _setLastWorkspacePerCat] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem('cui-last-workspace-per-category');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  const persistLastWorkspace = useCallback((catId: string, wsId: string) => {
    _setLastWorkspacePerCat(prev => {
      const next = { ...prev, [catId]: wsId };
      try { localStorage.setItem('cui-last-workspace-per-category', JSON.stringify(next)); } catch {} // silent-ok: localStorage may be disabled
      return next;
    });
  }, []);

  // Listen for update-available notifications via WebSocket (forwarded by App.tsx)
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      try {
        const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (msg.type === 'cui-update-available') {
          setPendingCount(msg.count || 0);
        }
      } catch { /* ignore */ }
    }
    window.addEventListener('message', handleMessage);
    // Also check on mount (delayed to allow WS to connect first)
    setTimeout(() => {
      if (window.__cuiServerAlive === false) return;
      /*
       * NICHT FUER EINEN PARTNER (Rafael, 22.09.2026: „Weglassen, was ihm nicht
       * gehoert."). `/api/cui-sync/pending` ist der Abgleich der Arbeitsplaetze
       * — ein Betriebsweg, der fuer jeden ohne Admin-Recht 403 antwortet. Der
       * Ruf lief trotzdem bei jedem Laden und hinterliess in Herberts Konsole
       * eine rote Zeile ueber ein Werkzeug, das er gar nicht bedient. An der
       * QUELLE weglassen, nicht die Meldung verstecken: die Anzeige daneben
       * (der Sync-Knopf) ist ohnehin schon auf den Betreiber beschraenkt.
       */
      if (!istBetreiber) return;
      fetch('/api/cui-sync/pending', { signal: AbortSignal.timeout(10000) })
        .then(r => { if (!r.ok) throw new Error(`cui-sync/pending ${r.status}`); return r.json(); })
        .then(d => { if (d?.count > 0) setPendingCount(d.count); })
        .catch((err) => { console.warn('[ProjectTabs] fetchPending:', err); });
    }, 2000);
    return () => window.removeEventListener('message', handleMessage);
  }, [istBetreiber]);

  const handleSync = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    if (syncState === 'syncing') return;
    setSyncState('syncing');
    setSyncDetail('Building...');
    try {
      const resp = await fetch('/api/cui-sync', { method: 'POST', signal: AbortSignal.timeout(15000) });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `cui-sync ${resp.status}`);
      setSyncState('done');
      setSyncDetail(data.build || 'ok');
      setPendingCount(0);
      setTimeout(() => window.location.reload(), 3000);
    } catch (err: any) {
      console.warn('[ProjectTabs] handleSync:', err);
      setSyncState('error');
      setSyncDetail(err.message.slice(0, 60));
      setTimeout(() => { setSyncState('idle'); setSyncDetail(''); }, 5000);
    }
  }, [syncState]);

  const handleLayoutClick = useCallback(async (projectId: string) => {
    if (layoutClickState === 'running') return;
    setLayoutClickState('running');
    setLayoutClickDetail('');
    try {
      // Server-side auto-layout rebuilds the layout atomically and broadcasts
      // control:apply-layout — wins over the client-only fallback dispatched
      // below (which leaks ghost cui-tabs because CuiLitePanel auto-loads a
      // SID from localStorage when initialSessionId is missing). Every
      // outcome — success, a legitimate "nothing to arrange", or a genuine
      // error — must be surfaced. Previously this was a bare `fetch(...)
      // .catch(...)`: fetch() only rejects on a network failure, never on an
      // HTTP error status or a 200 "triggered:false" body, so any of those
      // looked identical to the user as "clicked, nothing happened"
      // (reported 2026-08-12).
      const resp = await fetch('/api/control/auto-layout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
        signal: AbortSignal.timeout(15000),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `auto-layout ${resp.status}`);
      if (data.triggered === false) {
        setLayoutClickState('nothing');
        setLayoutClickDetail(data.reason || 'Nichts anzuordnen');
      } else {
        setLayoutClickState('done');
        const n = data.total ?? 0;
        setLayoutClickDetail(`${n} Chat${n === 1 ? '' : 's'} angeordnet`);
      }
    } catch (err: any) {
      console.warn('[ProjectTabs] handleLayoutClick:', err);
      window.dispatchEvent(new CustomEvent('cui-auto-layout', { detail: { projectId } }));
      setLayoutClickState('error');
      setLayoutClickDetail(err.message?.slice(0, 60) || 'Fehler — Fallback ausgeloest');
    } finally {
      setTimeout(() => { setLayoutClickState('idle'); setLayoutClickDetail(''); }, 4000);
    }
  }, [layoutClickState]);

  const checkPanelHealth = useCallback(async () => {
    if (window.__cuiServerAlive !== true) return;
    try {
      const resp = await fetch('/api/panel-health', { signal: AbortSignal.timeout(12000) });
      if (!resp.ok) return;
      const data = await resp.json();
      setPanelHealth({
        running: data.running,
        total: data.total,
        missing: data.panels.filter((p: any) => !p.running).map((p: any) => p.name)
      });
    } catch { /* timeout on slow connections */ }
  }, []);

  const handleStartPanels = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    if (syncState === 'syncing') return;
    setSyncState('syncing');
    setSyncDetail('Starting panels...');
    try {
      const resp = await fetch('/api/start-all-panels', { method: 'POST', signal: AbortSignal.timeout(15000) });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `start-all-panels ${resp.status}`);
      setSyncState('done');
      setSyncDetail(data.message || 'Panels starting');
      // Re-check health after 10s
      setTimeout(() => {
        checkPanelHealth();
        setSyncState('idle');
        setSyncDetail('');
      }, 10000);
    } catch (err: any) {
      console.warn('[ProjectTabs] handleStartPanels:', err);
      setSyncState('error');
      setSyncDetail(err.message.slice(0, 60));
      setTimeout(() => { setSyncState('idle'); setSyncDetail(''); }, 5000);
    }
  }, [syncState, checkPanelHealth]);

  // Check panel health on mount
  useEffect(() => {
    checkPanelHealth();
    const interval = setInterval(checkPanelHealth, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [checkPanelHealth]);

  // Fetch categories from API; fall back to FALLBACK_CATEGORIES on 404 or error
  useEffect(() => {
    fetch('/api/workspace-categories', { signal: AbortSignal.timeout(5000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.categories?.length > 0) {
          setCategories([...data.categories].sort((a: WorkspaceCategory, b: WorkspaceCategory) => a.order - b.order));
        }
      })
      .catch(() => { /* keep FALLBACK_CATEGORIES */ });
  }, []);

  // Filter projects by allowedWorkspaces, then sort: needs_attention first, working, idle, no status
  // MUST be declared before any hook that depends on it (projectsByCategory, …)
  // to avoid TDZ errors at render time.
  const sortedProjects = useMemo(() => {
    const scoreFn = (p: Project) => {
      const state = attention?.[p.id];
      if (state === 'needs_attention') return 0;
      if (state === 'working') return 1;
      if (state === 'waiting') return 2;
      if (state === 'idle') return 3;
      return 4;
    };
    return [...projects]
      .filter(p => canAccessWorkspace(p.id))
      .sort((a, b) => scoreFn(a) - scoreFn(b));
  }, [projects, attention, canAccessWorkspace]);

  // Group projects by category for multi-row rendering (Full View)
  const projectsByCategory = useMemo(() => {
    const map: Record<string, Project[]> = {};
    for (const p of sortedProjects) {
      const cat = getProjectCategory(p);
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    }
    return map;
  }, [sortedProjects]);

  // Active-Bar projects: the active workspace, anything working/needs_attention, and
  // every workspace still holding an unfinished chat.
  //
  // The last clause is the rule "a workspace with an unfinished chat must be open"
  // (Rafael, 2026-08-12). Without it the bar could hide an open chat completely:
  // App.tsx's attention pass skips liveness==='stuck' (its Rule 3, "needs looking at
  // NOW"), so a dead-but-never-finished session yielded no attention and therefore no
  // tab — while the AC badge counted it by Rule 1 ("open until Finish is clicked").
  // That was the observed 14-vs-13 mismatch: AC said 14, the visible tabs summed to
  // 13, and the missing one sat in "Arch Worker", which had no tab at all.
  //
  // Visibility now follows the same Rule-1 count AC uses, so the two agree. The
  // colour still follows the attention rule, so a stuck orphan gets a tab without
  // painting it permanently orange — that was the point of the 2026-08-04 skip and
  // it stays intact. Both rules are named and explained once in utils/chatLifecycle.ts;
  // do not re-derive either of them here.
  const activeOnlyProjects = useMemo(() => {
    return sortedProjects.filter(p => {
      const a = attention?.[p.id];
      return p.id === activeId || a === 'working' || a === 'waiting' || a === 'needs_attention'
        || (openCountByProject?.[p.id] ?? 0) > 0;
    });
  }, [sortedProjects, attention, activeId, openCountByProject]);

  // Click on a workspace tab: persist last-workspace-per-category, auto-close Full View, then select
  const handleWorkspaceSelect = useCallback((wsId: string) => {
    const proj = sortedProjects.find(p => p.id === wsId);
    if (proj) persistLastWorkspace(getProjectCategory(proj), wsId);
    setShowAllWorkspaces(false);
    onSelect(wsId);
  }, [sortedProjects, onSelect, persistLastWorkspace]);

  // Workspace pill — used by both Active-Bar and Full-View
  const renderProjectPill = (p: Project) => {
    const origIdx = projects.indexOf(p);
    return (
      <div
        key={p.id}
        ref={p.id === activeId ? (el) => { el?.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          background: attention?.[p.id] === 'needs_attention'
            ? 'rgba(255, 158, 100, 0.25)'
            : attention?.[p.id] === 'working'
              ? 'rgba(158, 206, 106, 0.18)'
              : attention?.[p.id] === 'waiting'
                ? 'rgba(255, 216, 102, 0.14)'
                : p.id === activeId ? 'var(--tn-surface)' : 'transparent',
          borderBottom: attention?.[p.id] === 'needs_attention'
            ? '3px solid var(--tn-orange-hell)'
            : attention?.[p.id] === 'working'
              ? '3px solid var(--tn-green)'
              : attention?.[p.id] === 'waiting'
                ? '3px solid var(--tn-gelb)'
                : p.id === activeId ? '2px solid var(--tn-blue)' : '2px solid transparent',
          borderRadius: '4px 4px 0 0',
          transition: 'all 0.15s',
          flexShrink: 0,
        }}
      >
        <button
          data-ai-id={`project-tabs-pill-select-${p.id}`}
          onClick={() => handleWorkspaceSelect(p.id)}
          onDoubleClick={(e) => { e.preventDefault(); onEdit(p.id); }}
          title={`${p.name} — ${p.workDir}${openCountByProject?.[p.id] ? `\n${openCountByProject[p.id]} offene Chats — verschwinden erst nach Finish` : ''}\nDoppelklick zum Bearbeiten${origIdx < 9 ? `\nCmd+${origIdx + 1}` : ''}`}
          style={{
            background: 'none',
            color: p.id === activeId ? 'var(--tn-text)' : 'var(--tn-text-muted)',
            border: 'none',
            padding: '2px 8px 2px 10px',
            fontSize: 11,
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            WebkitAppRegion: 'no-drag',
          } as React.CSSProperties}
        >
          {attention?.[p.id] && (
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: attention[p.id] === 'needs_attention' ? 'var(--tn-orange-hell)'
                : attention[p.id] === 'working' ? 'var(--tn-green)'
                : attention[p.id] === 'waiting' ? 'var(--tn-gelb)'
                : 'var(--tn-dim)',
              boxShadow: attention[p.id] === 'needs_attention'
                ? '0 0 8px color-mix(in srgb, var(--tnf-ff9e64) 66.7%, transparent), 0 0 3px var(--tn-orange-hell)'
                : attention[p.id] === 'working'
                ? '0 0 6px color-mix(in srgb, var(--tnf-9ece6a) 53.3%, transparent)'
                : attention[p.id] === 'waiting'
                ? '0 0 6px color-mix(in srgb, var(--tnf-ffd866) 53.3%, transparent)'
                : 'none',
              display: 'inline-block', marginRight: 5, flexShrink: 0,
              animation: attention[p.id] === 'needs_attention' ? 'pulse-attention 0.8s ease-in-out infinite'
                : attention[p.id] === 'working' ? 'pulse 1.5s ease-in-out infinite'
                : attention[p.id] === 'waiting' ? 'pulse 2.5s ease-in-out infinite'
                : 'none',
            }} />
          )}
          {origIdx < 9 && (
            <span style={{ fontSize: 9, opacity: 0.4, marginRight: 4, fontFamily: 'monospace' }}>
              {origIdx + 1}
            </span>
          )}
          {p.name}{openCountByProject?.[p.id] ? ` (${openCountByProject[p.id]})` : ''}
        </button>
        {projects.length > 1 && (
          <button
            data-ai-id={`project-tabs-pill-delete-${p.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(p.id);
            }}
            title={`Delete ${p.name}`}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--tn-text-muted)',
              cursor: 'pointer',
              fontSize: 10,
              padding: '2px 6px 2px 0',
              opacity: 0.5,
              transition: 'opacity 0.15s',
              WebkitAppRegion: 'no-drag',
            } as React.CSSProperties}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = '1'; (e.target as HTMLElement).style.color = 'var(--tn-red)'; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = '0.5'; (e.target as HTMLElement).style.color = 'var(--tn-text-muted)'; }}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  const handleRebuild = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    if (syncState === 'syncing') return;
    setSyncState('syncing');
    setSyncDetail('Rebuilding frontend...');
    try {
      // No token here: the endpoint authorises by session role (admin). The old
      // Bearer token came from window.CUI_REBUILD_TOKEN, which the server printed
      // into the pre-auth page source for every visitor. The session cookie rides
      // along automatically and cannot be read out of the HTML.
      const resp = await fetch('/api/rebuild-frontend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(15000)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `rebuild-frontend ${resp.status}`);
      setSyncState('done');
      setSyncDetail(data.detail || 'ok');
      setTimeout(() => window.location.reload(), 2000);
    } catch (err: any) {
      console.warn('[ProjectTabs] handleRebuild:', err);
      setSyncState('error');
      setSyncDetail(err.message.slice(0, 60));
      setTimeout(() => { setSyncState('idle'); setSyncDetail(''); }, 5000);
    }
  }, [syncState]);

  const syncColors: Record<SyncState, string> = {
    idle: 'var(--tn-text-muted)',
    syncing: 'var(--tn-info)',
    done: 'var(--tn-ok)',
    error: 'var(--tn-fehler)',
  };

  const layoutClickColors: Record<LayoutClickState, string> = {
    idle: 'var(--tn-text-muted)',
    running: 'var(--tn-info)',
    done: 'var(--tn-ok)',
    nothing: 'var(--tn-text-muted)',
    error: 'var(--tn-fehler)',
  };

  const hasPending = pendingCount > 0 && syncState === 'idle';


  return (
    <div className={`cui-project-header${guideActive && ui.focus ? ' cui-header-focus' : ''}${kopfEingeklappt ? ' cui-kopf-eingeklappt' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--tn-bg-dark)',
        borderTop: `2px solid ${MODE_COLORS[CUI_MODE]}`,
        borderBottom: '1px solid var(--tn-border)',
        flexShrink: 0,
      } as React.CSSProperties}
    >
    {/*
      DER EINGEKLAPPTE KOPF (Tablet, Rafael 03.10.2026 09:26). Ein Tipp holt die Leiste
      zurueck. Warnungen verschwinden dabei NICHT: was oben als Warnung stuende (fehlende
      Panels, Syncthing, wartende Aenderungen, knappe KI-Zugaenge), steht hier als Chip.
      Die Leiste darunter bleibt gemountet (nur ausgeblendet) — offene Menues und
      Zaehler laufen weiter.
    */}
    {kopfEingeklappt && (() => {
      const warnungen: { text: string; farbe: string }[] = [];
      if (istBetreiber && panelHealth && panelHealth.missing.length > 0) warnungen.push({ text: fensterAusText(panelHealth.missing.length), farbe: 'var(--tn-fehler)' });
      if (istBetreiber && syncthing.warnung) warnungen.push({ text: syncthing.paused ? 'ST pausiert' : `ST ${syncthing.lastSyncLabel ?? ''}`.trim(), farbe: 'var(--tn-fehler)' });
      if (istBetreiber && (pendingCount > 0 || syncState !== 'idle')) warnungen.push({ text: pendingCount > 0 ? `Update (${pendingCount})` : 'Sync läuft', farbe: 'var(--tn-orange)' });
      if (kiStand.rot) warnungen.push({ text: !istBetreiber ? 'KI knapp' : `KI ${kiStand.hoechste === null ? '!' : `${kiStand.hoechste} %`}`, farbe: 'var(--tn-red)' });
      return (
        <button type="button" className="cui-kopf-streifen" data-ai-id="cui-kopf-streifen"
          onClick={() => setKopfEingeklappt(false)}
          aria-label={`Kopfleiste zeigen${warnungen.length ? ` — ${warnungen.map(w => w.text).join(', ')}` : ''}`}
          title="Kopfleiste wieder zeigen">
          <span aria-hidden="true" style={{ color: 'var(--tn-text-muted)' }}>▾ Leiste</span>
          {guideActive && <span style={{ color: 'var(--tn-green)', fontWeight: 700 }}>Guide</span>}
          {/* „● N offen" bleibt auch eingeklappt sichtbar; ein Tipp holt die Leiste, dort ist er anklickbar. */}
          {partnerOffen.total > 0 && (() => {
            const t = partnerOffenText(partnerOffen);
            return (
              <span data-ai-id="cui-kopf-streifen-offen" className="cui-kopf-streifen-chip" title={`${t.kurz}: ${t.aufschluesselung}`}
                style={{ color: PARTNER_OFFEN_FARBE, borderColor: PARTNER_OFFEN_FARBE, background: 'rgba(247,118,142,0.14)' }}>
                ● {t.kurz}
              </span>
            );
          })()}
          {warnungen.map(w => (
            <span key={w.text} data-ai-id="cui-kopf-streifen-warnung" className="cui-kopf-streifen-chip" style={{ color: w.farbe, borderColor: w.farbe }}>
              ● {w.text}
            </span>
          ))}
        </button>
      );
    })()}
    {/*
      EINE ZEILE, WO ES PASST (Rafael, 03.10.2026 15:39: „können wir das hier nicht eigentlich alles in
      eine Leiste quetschen? mit Übersicht, Chats, Guide und so weiter? oder in zwei"). Logo und
      Navigation, die KI-Boxen und die Bedienung stehen in EINEM umbrechenden Behaelter. Passen alle
      drei nebeneinander (gemessen in useKopfEinzeilig), steht die KI-Leiste in der Mitte; sonst
      oben Logo + Navigation + Bedienung, darunter die KI-Boxen. Nichts wird versteckt.
    */}
    <div ref={kopfZeileRef} className={`cui-kopf-zeile ${kopfEinzeilig ? 'cui-kopf-einzeilig' : 'cui-kopf-zweizeilig'}`}>
    {/* Row 1: Drag bar with logo + MC + AC */}
    <div className="cui-title-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '2px 8px 2px 80px',
        minHeight: 28,
        WebkitAppRegion: 'drag',
      } as React.CSSProperties}
    >
      <img src="/werking-logo.png" alt="W" style={{ width: 20, height: 20, borderRadius: 4, marginRight: 4 }} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginRight: 4,
          whiteSpace: 'nowrap',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <span style={{ color: 'var(--tn-marke-text)' }}>Werk</span><span style={{ color: 'var(--tn-gold)' }}>ING</span><span style={{ color: 'var(--tn-marke-grau)', fontWeight: 500 }}> Partner</span>
      </span>
      <span
        title={CUI_MODE === 'local'
          ? 'LOCAL MODE\n\nProjekte: ~/.cui/local-data/projects/\nLayouts: ~/.cui/local-data/layouts/\nWorkspaces: ~/Projects/{id}\nChats: Remote (CUI Binary)'
          : 'REMOTE MODE\n\nProjekte: data/projects/\nLayouts: data/layouts/\nWorkspaces: /root/orchestrator/workspaces/\nChats: /home/claude-user/.cui-account{1-4}/'}
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--tn-bg)',
          background: MODE_COLORS[CUI_MODE],
          padding: '1px 6px',
          borderRadius: 4,
          marginRight: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          cursor: 'help',
        }}
      >
        {CUI_MODE}
      </span>

      {/* Mission Control - permanent tab */}
      {/*
        ÜBERSICHT UND CHATS NUR UNTER OPTIONEN (Rafael 03.11.2026 16:10Z: „oben die Übersichtsfunktion
        weggeben, die Chats-Funktion weggeben oder nur unter Optionen“). Beide stehen jetzt im Menü
        „⋯ Werkzeuge & Ansicht“ (App.tsx `menueInhalt`); Tastenkürzel bleiben.
      */}
      {/*
        EIN GUIDE, ZWEI HAEUSER, KEIN UMSCHALTER (GUIDE.md "Ein Guide, zwei
        Coaches, die Karte waehlt das Haus"; Rafael, 09.09.2026 06:5xZ: "ich
        finde das klicken ein bisschen dumm, wenn eh jede karte zugeordnet
        ist"). Ersetzt den GD/Remote-Guide-Umschalter (bis 09.09. 06:5xZ
        Zielform, siehe 4e7427b2a) — der Guide zeigt beide Haeuser in EINER
        Oberflaeche, welches Haus eine Karte oeffnet, entscheidet die Karte
        selbst, nicht ein vorher gewaehlter Modus.
      */}
      {/* Partner (Rafael 03.11.2026, Gruppenraum-Auftrag c): Guide ist der einzige Modus — der Knopf erscheint
          nur noch als Rückweg, wenn ein Werkzeug den Guide kurz verlassen hat. Admin unverändert. */}
      {onGuideClick && !(partnerGuideKnopf && guideActive) && (
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--tn-border)', borderRadius: 5, padding: 1, marginRight: 4 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: guideActive ? 'var(--tn-surface)' : 'transparent',
            borderBottom: guideActive ? '2px solid var(--tn-green)' : '2px solid transparent',
            borderRadius: '4px 4px 0 0',
            marginRight: 4,
          }}
        >
          <button
            data-ai-id="project-tabs-guide"
            data-hervorgehoben={partnerGuideKnopf && !guideActive ? 'ja' : undefined}
            aria-pressed={guideActive ? true : false}
            onClick={onGuideClick}
            title={partnerGuideKnopf
              ? (guideActive ? 'Guide schließen und zum Arbeitsbereich wechseln' : GUIDE_KNOPF_ERKLAERUNG)
              : 'Guide: die eine Entscheidung, die gerade bei dir haengt (Spalte links neben dem Workspace)'}
            style={{
              /* Offen: im Hellmodus war der Knopf nur gruene Schrift ohne Flaeche (Rafael, 05.10.2026:
               * „nicht wirklich erkennbar"). Fuer Partner deshalb auch offen ein Rahmen. */
              ...(partnerGuideKnopf && !guideActive ? GUIDE_KNOPF_HERVORGEHOBEN
                : partnerGuideKnopf ? { background: 'var(--tn-bg-highlight)', border: '1px solid var(--tn-green)', borderRadius: 4 }
                : { background: 'none', border: 'none' }),
              color: partnerGuideKnopf && !guideActive ? 'var(--tn-bg)' : guideActive ? 'var(--tn-green)' : 'var(--tn-text-muted)',
              padding: '4px 12px',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: guideActive || partnerGuideKnopf ? 700 : 400,
              WebkitAppRegion: 'no-drag',
            } as React.CSSProperties}
          >
            {partnerGuideKnopf ? GUIDE_KNOPF_TEXT : 'Guide'}
          </button>
        </div>
      </div>
      )}


      <div className="cui-kopf-feder" style={{ flex: 1 }} />

      {/*
        Die vier Anordnungssymbole und B1/B2 standen bis 03.10.2026 hier. Sie sind jetzt
        beschriftete Gruppen im Knopf „Ansicht ▾" (AnsichtMenue.tsx, Rafael 03.10.2026 10:00).
      */}
    </div>{/* end Row 1 */}

    {/* Row 2: Account usage pills + toolbar */}
    <div className="cui-kopf-bedienung"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        flexWrap: 'wrap',
        WebkitAppRegion: 'no-drag',
      } as React.CSSProperties}
    >
      {/*
        * ZUSAMMENGEFUEHRT (21.09.2026). Auf develop war bereits geklammert, was
        * ANGEZEIGT wird — Kontingente und Kontosteuerung. Rafaels Entscheidung
        * vom selben Tag (Option a) nennt aber beides: "Kontingente, Rebuild,
        * Sync, Cache und den internen Verlauf fuer Nicht-Admins ausblenden".
        * Die Knoepfe, die etwas AUSLOESEN, standen weiter fuer jeden da.
        * Deshalb reicht die Klammer jetzt bis hinter den Sync-Knopf.
        *
        * Der Layout-Knopf dahinter bleibt bewusst fuer alle — er ordnet die
        * EIGENEN Fenster und loest nichts am Server aus.
        */}
      {/*
        DIE KI-ZUGAENGE SIEHT JEDER — Rafael, 22.09.2026, auf die Frage, ob ein Partner
        die Leiste mit dem Verbrauch bekommen soll: *„c — Alles wie bei dir."* Gefragt
        hatte ich, weil das fremdes Budget zeigt; seine Antwort ist eindeutig.

        Aus der Betreiber-Gruppe geloest ist NUR die Leiste (seit 03.10.2026 als
        Sammelanzeiger mit Aufklapp-Menue). Die Wahl des Zugangs („KI-Zugänge verwalten",
        AccountControl) steht dagegen nur beim Betreiber: `GET /api/mission/accounts` ist
        `requireRole('admin')` (server/routes/mission.ts), ein Partner sah dort nur
        „Kontostand nicht erreichbar (403)" — ein toter Knopf (Rafael, 03.10.2026 09:31:
        „für Partner ausblenden, wo nötig"). Die fruehere Begruendung „gilt dem eigenen
        Rechner" stimmte also nicht. Was darunter folgt — Syncthing, Live-Schalter — sind
        Betriebswege und bleiben beim Eigentuemer; danach hat er nicht gefragt.
        Die Daten dafuer waren ohnehin schon offen (`/api/claude-code/stats-v2` und
        `/api/accounts/health` antworten einem Partner mit 200); es fehlte allein die
        Anzeige.
      */}
      {/*
        ZWEI EHEMALIGE EIGENE ZEILEN ALS CHIPS (Rafael, 03.10.2026 10:15, zugestimmt):
        der Streifen „N offen · N Inbox" ueber der Leiste und, bei uiMode „sales", die
        Zeile „Du bist im Arbeitsbereich – ← Zurück zum Sales-Bereich". Die Sales-Zeile
        war am 02.09.2026 entstanden, weil Rafael den alten 12px-Knopf unten rechts nicht
        fand — der Chip steht deshalb blau gefuellt ganz vorn in der Leiste (order -2),
        nicht klein irgendwo dazwischen.
      */}
      {onZurueckZuSales && (
        <button type="button" data-ai-id="workspace-back-to-sales" className="cui-kopf-sales-chip"
          onClick={onZurueckZuSales}
          title="Du bist im Arbeitsbereich — zurück zum Sales-Bereich">
          ← Sales
        </button>
      )}
      {canAccessPanel('group-chat') && <GruppenraumKnopf ungelesen={partnerOffen.gruppe} onOpen={onPartnerOffen} />}
      <PartnerOffenChip counts={partnerOffen} onOpen={onPartnerOffen} />
      {/* KI-Zugaenge stehen fuer alle als Boxen in der KI-Leiste (KiLeiste.tsx) — Betreiber seit
          03.10.2026 14:09, Partner seit 16:35 (Option a: „Alle vier, neutral benannt"); der
          fruehere Sammel-Chip „KI 2/4 frei · höchste 102 %" ist damit weg. */}

      {/*
        WERKZEUGE HINTER ⋯ (Rafael, 03.10.2026: „oben gibts Sachen wie Rebuild, Cache und so
        weiter – die Leisten ganz oben sind chaotisch"). In der Leiste steht nur noch, was
        WARNT — fehlende Panels, ein pausierter oder lange stiller Syncthing, wartende
        Aenderungen bzw. ein laufender Sync. Alles Ausloesende liegt im Menue daneben.
        Die Betreiber-Klammer vom 21.09.2026 bleibt: Nicht-Admins sehen keine Werkzeuge.
      */}
      {istBetreiber && (
        <div className="cui-system-tools cui-kopf-warnungen" role="group" aria-label="Warnungen der Werkzeuge"
          style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {syncthing.warnung && <SyncthingToggle st={syncthing} />}
          {panelHealth && panelHealth.missing.length > 0 && (
            <button
              data-ai-id="project-tabs-start-missing-panels"
              onClick={handleStartPanels}
              disabled={syncState === 'syncing'}
              title={`${fensterAusText(panelHealth.missing.length)}:\n${panelHealth.missing.join('\n')}\n\nKlick startet sie.`}
              style={{
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid var(--tn-fehler)',
                    color: 'var(--tn-fehler)',
                    padding: '2px 6px',
                    fontSize: 9,
                    fontWeight: 600,
                    cursor: syncState === 'syncing' ? 'wait' : 'pointer',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
              } as React.CSSProperties}
            >
              {fensterAusText(panelHealth.missing.length)}
            </button>
          )}
          {(hasPending || syncState !== 'idle') && (
      <button
            data-ai-id="project-tabs-sync-warnung"
            onClick={handleSync}
            disabled={syncState === 'syncing'}
            title={
              hasPending
                ? `${pendingCount} Datei${pendingCount > 1 ? 'en' : ''} geaendert — Klick zum Updaten`
                : syncState === 'idle' ? 'Build + Restart (keine Aenderungen)'
                : syncDetail
            }
            style={{
              background: hasPending ? 'rgba(224,175,104,0.15)' : syncState === 'syncing' ? 'rgba(59,130,246,0.15)' : 'none',
              border: `1px solid ${hasPending ? 'var(--tn-orange)' : syncState === 'idle' ? 'var(--tn-border)' : syncColors[syncState]}`,
              color: hasPending ? 'var(--tn-orange)' : syncColors[syncState],
              padding: '2px 6px',
              fontSize: 9,
              fontWeight: 600,
              cursor: syncState === 'syncing' ? 'wait' : 'pointer',
              borderRadius: 3,
              whiteSpace: 'nowrap',
            } as React.CSSProperties}
          >
            {syncState === 'idle' && !hasPending && 'Sync'}
            {syncState === 'idle' && hasPending && `Update (${pendingCount})`}
            {syncState === 'syncing' && 'Syncing...'}
            {syncState === 'done' && 'Reloading...'}
            {syncState === 'error' && 'Sync Error'}
          </button>
          )}
          {syncDetail && syncState !== 'idle' && (
        <span style={{ fontSize: 8, color: syncColors[syncState] }}>{syncDetail}</span>
      )}
        </div>
      )}
      {(
        <KopfMenue aiId="cui-werkzeug-menue" menueTitel="Werkzeuge & Ansicht" breite={340}
          knopfTitel={istBetreiber ? 'Einstellungen und Werkzeuge: Syncthing, Live/Static, Rebuild, Cache, Sync, seltene Ansichts-Knöpfe' : 'Einstellungen: seltene Ansichts-Knöpfe'}
          knopfStil={{ background: 'var(--tn-bg-dark)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)', borderRadius: 5,
            padding: '0 9px', minHeight: 24, fontSize: 15, lineHeight: '20px', cursor: 'pointer' }}
          knopf={<span aria-label="Werkzeuge">⋯</span>}>
          {menueInhalt && <div className="cui-kopf-menue-gruppe" role="group" aria-label="Ansicht">{menueInhalt}</div>}
          {istBetreiber && <div className="cui-kopf-menue-gruppe cui-kopf-menue-werkzeuge" role="group" aria-label="Werkzeuge">
            <SyncthingToggle st={syncthing} />
      <button
        data-ai-id="project-tabs-live-mode-toggle"
        onClick={() => {
          const newState = !allLive;
          setAllLive(newState);
          window.dispatchEvent(new CustomEvent('cui-all-live', { detail: { live: newState } }));
        }}
        title={allLive ? 'Live-Modus aus (Static: WS Events only)' : 'Live-Modus fuer alle Chats (Echtzeit-Streaming)'}
        style={{
          background: allLive ? 'rgba(168,85,247,0.15)' : 'none',
          border: `1px solid ${allLive ? 'var(--tn-violett)' : 'var(--tn-border)'}`,
          color: allLive ? 'var(--tn-violett)' : 'var(--tn-text-muted)',
          padding: '2px 6px',
          fontSize: 9,
          fontWeight: allLive ? 700 : 400,
          cursor: 'pointer',
          borderRadius: 3,
          whiteSpace: 'nowrap',
        } as React.CSSProperties}
      >
        {allLive ? '\u25CF Live' : '\u23F8 Static'}
      </button>
      <button
        data-ai-id="project-tabs-rebuild"
        onClick={handleRebuild}
        disabled={syncState === 'syncing'}
        title="Rebuild CUI frontend (npm run build + restart server)"
        style={{
          background: 'none',
          border: '1px solid var(--tn-border)',
          color: 'var(--tn-text-muted)',
          padding: '2px 6px',
          fontSize: 9,
          fontWeight: 600,
          cursor: syncState === 'syncing' ? 'wait' : 'pointer',
          borderRadius: 3,
          whiteSpace: 'nowrap',
        } as React.CSSProperties}
      >
        Rebuild
      </button>
      <button
        data-ai-id="project-tabs-clear-cache"
        onClick={async () => {
          // Factory reset: wipe server-saved layout + template for the active project so
          // the next load falls back to defaultLayout() — true standard, not the user's
          // customized layout.
          if (activeId) {
            try {
              await Promise.all([
                fetch(`/api/layouts/${activeId}`, { method: 'DELETE' }),
                fetch(`/api/layouts/${activeId}/template`, { method: 'DELETE' }),
              ]);
            } catch (err) { console.warn('[Cache] server-side layout delete failed:', err); }
          }
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(r => r.unregister()));
          }
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k)));
          }
          window.dispatchEvent(new CustomEvent('nuclear-refresh'));
          // Drop layout + template caches too — otherwise the stale client cache
          // would rehydrate immediately and overwrite the server delete on next save.
          Object.keys(localStorage).forEach(k => {
            if (k.startsWith('cui-layout-') || k.startsWith('cui-template-')) {
              localStorage.removeItem(k);
            }
          });
          const keep = ['flexlayout', 'cui-workspace-'];
          Object.keys(localStorage).forEach(k => {
            if (!keep.some(prefix => k.startsWith(prefix))) localStorage.removeItem(k);
          });
          setTimeout(() => {
            window.location.href = window.location.pathname + '?_cb=' + Date.now();
          }, 200);
        }}
        title="Cache leeren + Server-Layout löschen → echter Standard beim Reload"
        style={{
          background: 'none',
          border: '1px solid var(--tn-border)',
          color: 'var(--tn-text-muted)',
          padding: '2px 6px',
          fontSize: 9,
          fontWeight: 600,
          cursor: 'pointer',
          borderRadius: 3,
          whiteSpace: 'nowrap',
        } as React.CSSProperties}
      >
        Cache
      </button>
      <button
        data-ai-id="project-tabs-sync"
        onClick={handleSync}
        disabled={syncState === 'syncing'}
        title={
          hasPending
            ? `${pendingCount} Datei${pendingCount > 1 ? 'en' : ''} geaendert — Klick zum Updaten`
            : syncState === 'idle' ? 'Build + Restart (keine Aenderungen)'
            : syncDetail
        }
        style={{
          background: hasPending ? 'rgba(224,175,104,0.15)' : syncState === 'syncing' ? 'rgba(59,130,246,0.15)' : 'none',
          border: `1px solid ${hasPending ? 'var(--tn-orange)' : syncState === 'idle' ? 'var(--tn-border)' : syncColors[syncState]}`,
          color: hasPending ? 'var(--tn-orange)' : syncColors[syncState],
          padding: '2px 6px',
          fontSize: 9,
          fontWeight: 600,
          cursor: syncState === 'syncing' ? 'wait' : 'pointer',
          borderRadius: 3,
          whiteSpace: 'nowrap',
        } as React.CSSProperties}
      >
        {syncState === 'idle' && !hasPending && 'Sync'}
        {syncState === 'idle' && hasPending && `Update (${pendingCount})`}
        {syncState === 'syncing' && 'Syncing...'}
        {syncState === 'done' && 'Reloading...'}
        {syncState === 'error' && 'Sync Error'}
      </button>
      {syncDetail && syncState !== 'idle' && (
        <span style={{ fontSize: 8, color: syncColors[syncState] }}>{syncDetail}</span>
      )}
          </div>}
        </KopfMenue>
      )}

      <div className="cui-workspace-controls" role="group" aria-label="Ansicht">
        {/* EIN Knopf statt fuenf Umschaltern (Rafael, 03.10.2026 10:00) — siehe AnsichtMenue.tsx. */}
        <AnsichtMenue
          guideActive={!!guideActive}
          guideEingeklappt={guideEingeklappt}
          anordnungModus={anordnungModus}
          anordnungFestgelegt={anordnungFestgelegt}
          onAnordnung={onAnordnung}
          screenId={screenId}
          onScreenChange={onScreenChange}
          fehlendeChats={missingSessions}
          layoutLaeuft={layoutClickState === 'running'}
          layoutMeldung={layoutClickState !== 'idle' ? layoutClickDetail : ''}
          layoutFarbe={layoutClickColors[layoutClickState]}
          onLayout={() => handleLayoutClick(activeId)}
        />
        <TagNachtSchalter />
      </div>

      {kopfStatus}

      <div className="cui-kopf-feder" style={{ flex: 1 }} />

      {/* „Geteilte Bühnen" (Eingang + Rückmeldungen) steht hier in der Kopfleiste. Bis 24.10.2026
          schwebte er fest unten rechts über allen Panels und verdeckte dort Bedienelemente. */}
      <StageSharesInbox />
      {/* Versionsabzeichen (git / bundle, Klick kopiert) — bis 24.10.2026 fest unten rechts ueber den Panels. */}
      <VersionBadge nurBanner={ohneBetriebsanzeigen} />

      {/* User badge + logout (only when auth enabled) */}
      {authEnabled && user && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 6px',
          background: 'rgba(122,162,247,0.1)',
          border: '1px solid rgba(122,162,247,0.3)',
          borderRadius: 3,
          fontSize: 9,
          color: 'var(--tn-link)',
          whiteSpace: 'nowrap',
        }}>
          {/* „Was wirkt auf mich" (Rafael, 20.10.2026): Klick auf den eigenen Namen. */}
          <button type="button" data-ai-id="project-tabs-was-wirkt" onClick={() => setWasWirktOffen(true)}
            title="Was wirkt auf mich — alle Anweisungen, die deine Berater lesen; deine persönliche Anleitung bearbeiten"
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: 600 }}>
            {user.name}
          </button>
          {wasWirktOffen && <WasWirktAufMich onClose={() => setWasWirktOffen(false)} />}
          {/* Rueckweg zur Variantenwahl (KD 91624155, 01.11.2026): zeigt den Picker ohne automatisches Oeffnen. */}
          {(user.variants?.length ?? 0) > 1 && (
            <a data-ai-id="project-tabs-variante-wechseln" href="/?variante=waehlen" title="Variante wechseln — Standard oder eigene Variante wählen"
              style={{ color: 'inherit', textDecoration: 'none', padding: '0 2px', fontWeight: 600 }}>
              ⇄
            </a>
          )}
          <button
            data-ai-id="project-tabs-logout"
            onClick={() => logout()}
            title="Abmelden"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--tn-red)',
              cursor: 'pointer',
              padding: '0 2px',
              fontSize: 9,
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            X
          </button>
        </div>
      )}
    </div>
    <a data-ai-id="kopf-emergency-terminal" href={(() => { const u = new URL(window.location.href); if (u.protocol === 'http:') { const machine = Object.values(emergencyPorts.machines).find(m => typeof m === 'object' && 'tailscale' in m && m.tailscale === u.hostname); if (machine && typeof machine === 'object' && 'emergency_origin' in machine) return String(machine.emergency_origin) + emergencyPorts.emergency_terminal.path; u.port = String(emergencyPorts.emergency_terminal.port); } u.pathname = emergencyPorts.emergency_terminal.path; u.search = ''; u.hash = ''; return u.href; })()} target="_blank" rel="noopener noreferrer" title="Unabhängigen Notfallzugang öffnen" style={{ marginRight: 8, padding: '6px 8px', whiteSpace: 'nowrap', borderRadius: 5, border: '1px solid #a78646', background: 'var(--tn-bg)', color: 'var(--tn-text)', textDecoration: 'none' }}>✚ Emergency Terminal</a>
    {istBetreiber ? (
      <KiLeiste accounts={ki.accounts} health={ki.health} zusatz={<>{user?.id === 'rafael' ? <><Fokuszeit />{onKalender && <button data-ai-id="kopf-kalender" onClick={onKalender} title="Vollständigen Kalender öffnen" style={{ marginRight: 8, padding: '6px 8px', whiteSpace: 'nowrap', borderRadius: 5, border: '1px solid var(--tn-border)', background: 'var(--tn-bg)', color: 'var(--tn-text)', cursor: 'pointer' }}>📅 Kalender</button>}</> : null}</>}>
        <KiMenueInhalt accounts={ki.accounts} health={ki.health} mitVerwaltung />
      </KiLeiste>
    ) : ohneBetriebsanzeigen ? null : (
      <KiLeiste accounts={ki.accounts} health={ki.health} partner />
    )}
    </div>{/* end cui-kopf-zeile */}

    {/*
      IM GUIDE-MODUS GIBT ES KEINE ARBEITSBEREICHS-REITER.

      Rafael, 06.09. 14:31Z: "wenn der Guide eingeschaltet ist, [sollen] die
      Workspaces oben nicht mehr umgeschaltet werden koennen. Sonst verleitet das
      immer dazu, dass ich da umschalte, aber der Guide ist ein voellig anderer
      Modus als die Arbeitspaces. [...] Der Guide ist ein eigener Modus, wo immer
      Schritt fuer Schritt das Folgende wichtig ist."

      Die Reiter sind im GD-Modus nicht nur ueberfluessig, sie sind der Ausstieg aus
      der Fuehrung: ein Klick dort landet in einem Arbeitsbereich mit dem, was darin
      zufaellig offen ist — genau der "rohe Arbeitsbereich", gegen den die Regie
      gebaut wurde. Gewechselt wird im GD-Modus ueber den Guide selbst (Karte,
      Thema), zurueck ueber den GD-Schalter, der sichtbar bleibt.
    */}
    {!guideActive && !partnerGuideKnopf && !showAllWorkspaces && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '1px 8px',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          flexWrap: 'wrap',
          minHeight: 24,
          WebkitAppRegion: 'no-drag',
        } as React.CSSProperties}
      >
        {activeOnlyProjects.map(renderProjectPill)}
        {activeOnlyProjects.length === 0 && (
          <span style={{ fontSize: 10, color: 'var(--tn-text-muted)', opacity: 0.5, padding: '2px 4px' }}>
            Keine aktiven Workspaces
          </span>
        )}
        <button
          data-ai-id="project-tabs-show-all-workspaces"
          onClick={() => setShowAllWorkspaces(true)}
          title="Alle Workspaces zeigen (klick auf Workspace zum Wechseln)"
          style={{
            background: 'none',
            border: '1px dashed var(--tn-border)',
            color: 'var(--tn-text-muted)',
            padding: '2px 8px',
            fontSize: 10,
            fontWeight: 600,
            cursor: 'pointer',
            borderRadius: 3,
            marginLeft: 6,
            flexShrink: 0,
            WebkitAppRegion: 'no-drag',
          } as React.CSSProperties}
        >
          ▼ Alle ({sortedProjects.length})
        </button>
      </div>
    )}

    {/* Full View: One row per category — opens on toggle, auto-closes on workspace select */}
    {!guideActive && !partnerGuideKnopf && showAllWorkspaces && (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '2px 8px',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            background: 'rgba(122,162,247,0.06)',
            minHeight: 22,
            WebkitAppRegion: 'no-drag',
          } as React.CSSProperties}
        >
          <span style={{ fontSize: 10, color: 'var(--tn-blue)', fontWeight: 600 }}>
            Alle Workspaces — Klick zum Wechseln
          </span>
          <button
            data-ai-id="project-tabs-hide-all-workspaces"
            onClick={() => setShowAllWorkspaces(false)}
            title="Vollansicht schliessen"
            style={{
              background: 'none',
              border: '1px solid var(--tn-border)',
              color: 'var(--tn-text-muted)',
              padding: '1px 8px',
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 3,
              WebkitAppRegion: 'no-drag',
            } as React.CSSProperties}
          >
            ▲ Schliessen
          </button>
        </div>
        {categories.map((cat, catIdx) => {
          const catProjects = projectsByCategory[cat.id] || [];
          const isLastCategory = catIdx === categories.length - 1;
          if (catProjects.length === 0 && !isLastCategory) return null;
          return (
            <div
              key={cat.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '1px 8px',
                borderTop: '1px solid rgba(255,255,255,0.04)',
                minHeight: 22,
                WebkitAppRegion: 'no-drag',
              } as React.CSSProperties}
            >
              <div
                title={cat.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  minWidth: 110,
                  maxWidth: 110,
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tn-text-muted)',
                  opacity: 0.7,
                  padding: '2px 4px',
                  borderRight: '1px solid rgba(255,255,255,0.06)',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span style={{ fontSize: 12 }}>{cat.icon}</span>
                <span>{cat.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                {catProjects.map(renderProjectPill)}
                {isLastCategory && (
                  <button
                    data-ai-id="project-tabs-new-project"
                    onClick={onNew}
                    title="Neues Projekt (Cmd+N)"
                    style={{
                      background: 'none',
                      border: '1px dashed var(--tn-border)',
                      color: 'var(--tn-text-muted)',
                      padding: '2px 8px',
                      fontSize: 10,
                      cursor: 'pointer',
                      borderRadius: 3,
                      marginLeft: 4,
                      flexShrink: 0,
                      WebkitAppRegion: 'no-drag',
                    } as React.CSSProperties}
                  >
                    + Projekt
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </>
    )}

    </div>
  );
});
